import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { recruitmentService } from "@/modules/recruitment/infrastructure/recruitment-service"
import { toast } from "sonner"
import axios from "axios"
import { InsufficientFundsError } from "@/lib/insufficient-funds"

export function useApplications(jobId?: string, stage?: string) {
  return useQuery({
    queryKey: ["applications", jobId, stage],
    queryFn: () => recruitmentService.getApplicationsForVacancy(jobId, stage),
  })
}

export function useApplication(id: string) {
  return useQuery({
    queryKey: ["application", id],
    queryFn: () => recruitmentService.getApplicationById(id),
    enabled: !!id,
  })
}

export function useCreateApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      candidateId,
    }: {
      jobId: string
      candidateId: string
    }) => recruitmentService.createApplication(jobId, candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      queryClient.invalidateQueries({ queryKey: ["job-applications"] })
      toast.success("Application created successfully!")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create application")
    },
  })
}

export function useUpdateApplicationStage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      stage,
      notes,
    }: {
      id: string
      stage: string
      notes?: string
    }) => recruitmentService.updateApplicationStage(id, stage, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      queryClient.invalidateQueries({ queryKey: ["job-applications"] })
      toast.success("Application stage updated!")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update stage")
    },
  })
}

export function useDeleteApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => recruitmentService.deleteApplication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      queryClient.invalidateQueries({ queryKey: ["job-applications"] })
      toast.success("Application deleted!")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete application")
    },
  })
}

export function useRescoreApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      organizationId,
      applicationId,
    }: {
      organizationId: string
      applicationId: string
    }) => recruitmentService.rescoreApplication(organizationId, applicationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      queryClient.invalidateQueries({ queryKey: ["job-applications"] })
      queryClient.invalidateQueries({
        queryKey: ["application", variables.applicationId],
      })
      toast.success("Rescore complete.")
    },
    onError: (error: unknown) => {
      // 402 InsufficientBalance is already surfaced by the api-client interceptor
      // via the shared "Top up wallet" toast — don't double-notify.
      if (error instanceof InsufficientFundsError) return

      const serverMessage = axios.isAxiosError(error)
        ? (error.response?.data as { error?: string } | undefined)?.error
        : undefined
      toast.error(serverMessage || "Failed to rescore application")
    },
  })
}
